// The driver's Delivered with no signal (decision 109): kept on the phone and sent when the signal is back. A delivery
// sent twice is safe on the server: the same quantities are written again, the cash at a stop is taken once, and an
// invoiced stop already delivered answers INVOICED_DELIVERED (taken here as delivered). Each delivery carries the time
// at the door, so the stop, the order and the cash keep the day they happened.
import { INVOICED_DELIVERED, StopDeliver } from '@morales/shared';
import { z } from 'zod';
import { ApiError } from './api';

const KEY = 'morales.deliveryQueue.v1';
const NOT_SENT_KEY = 'morales.deliveryNotSent.v1';

const Queued = z.object({ stopId: z.string(), date: z.string(), customerName: z.string(), body: StopDeliver, savedAt: z.string(), userId: z.string().optional() });
export type QueuedDelivery = z.infer<typeof Queued>;
export type NotSent = { delivery: QueuedDelivery; why: string };

function read<T>(key: string, schema: z.ZodType<T>): T[] {
  try {
    const raw = localStorage.getItem(key);
    const list: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    // an entry that no longer reads (an older version, a damaged one) is left out rather than sent and refused
    return list.flatMap((x) => {
      const r = schema.safeParse(x);
      return r.success ? [r.data] : [];
    });
  } catch {
    return [];
  }
}

function write(key: string, list: unknown[]): void {
  localStorage.setItem(key, JSON.stringify(list));
  for (const l of listeners) l();
}

const listeners = new Set<() => void>();
/** Called whenever what is kept on the phone changes (here or, through the storage event, in another tab). */
export function onDeliveryQueueChange(fn: () => void): () => void {
  listeners.add(fn);
  const storage = (e: StorageEvent) => {
    if (e.key === KEY || e.key === NOT_SENT_KEY) fn();
  };
  window.addEventListener('storage', storage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener('storage', storage);
  };
}

/** What waits on the phone; with a user, only that user's (a phone passed between drivers sends each one's own). */
export function queuedDeliveries(userId?: string): QueuedDelivery[] {
  const all = read(KEY, Queued);
  return userId ? all.filter((d) => !d.userId || d.userId === userId) : all;
}

/**
 * Keeps a delivery to send later; a second Delivered of the same stop replaces the first. False when the phone has no
 * room left (a full storage): the driver is told, nothing is half-kept.
 */
export function queueDelivery(d: QueuedDelivery): boolean {
  try {
    write(KEY, [...queuedDeliveries().filter((x) => x.stopId !== d.stopId), d]);
    return true;
  } catch {
    return false;
  }
}

/** Deliveries the server refused, kept with the reason (and the cash typed) until the driver dismisses them. */
export function notSentDeliveries(userId?: string): NotSent[] {
  const all = read(NOT_SENT_KEY, z.object({ delivery: Queued, why: z.string() }));
  return userId ? all.filter((n) => !n.delivery.userId || n.delivery.userId === userId) : all;
}

export function dismissNotSent(stopId: string, savedAt: string): void {
  write(NOT_SENT_KEY, notSentDeliveries().filter((n) => n.delivery.stopId !== stopId || n.delivery.savedAt !== savedAt));
}

/** Worth trying again later: no signal, signed out, a timeout, too many requests, the server down or deploying. */
export function isTransient(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status === 0 || err.status === 401 || err.status === 408 || err.status === 429 || err.status >= 500;
}

let inflight: Promise<{ sent: number; refused: NotSent[]; left: number; signedOut: boolean }> | null = null;
/** Deliveries being sent right now (a new version of the app waits for them, decision 183). */
export const deliveriesSending = (): boolean => inflight !== null;

/**
 * Sends what is kept for this user, oldest first. One send at a time (a second call joins the first). Stops at the
 * first failure worth retrying and keeps the rest. A refusal is not retried forever: it moves to the not-sent list
 * with its reason, so the driver (and the office) can see what did not land.
 */
export function flushDeliveries(send: (stopId: string, body: StopDeliver) => Promise<unknown>, userId?: string): Promise<{ sent: number; refused: NotSent[]; left: number; signedOut: boolean }> {
  if (inflight) return inflight;
  inflight = (async () => {
    let sent = 0;
    let signedOut = false;
    const refused: NotSent[] = [];
    for (const d of queuedDeliveries(userId)) {
      try {
        await send(d.stopId, { ...d.body, deliveredAt: d.body.deliveredAt ?? d.savedAt });
        sent += 1;
      } catch (err) {
        if (isTransient(err)) {
          signedOut = err instanceof ApiError && err.status === 401;
          break;
        }
        // delivered already (a send whose answer was lost): done, not refused
        if (!(err instanceof ApiError && err.status === 409 && err.problem.detail === INVOICED_DELIVERED)) {
          const n = { delivery: d, why: err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : String(err) };
          refused.push(n);
          try {
            write(NOT_SENT_KEY, [...notSentDeliveries().filter((x) => x.delivery.stopId !== d.stopId || x.delivery.savedAt !== d.savedAt), n]);
          } catch {
            // no room for the reason: it is still returned to the screen
          }
        }
      }
      write(KEY, queuedDeliveries().filter((x) => x.stopId !== d.stopId || x.savedAt !== d.savedAt));
    }
    return { sent, refused, left: queuedDeliveries(userId).length, signedOut };
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}
